import React, { useState } from "react";
import Turnstone from "turnstone";
import SplitMatch from "split-match";
import RecentSearchesPlugin from "turnstone-recent-searches";
import { XIcon } from "@heroicons/react/solid";
import { useStaticQuery, graphql, Link, navigate } from "gatsby";
import { BREAKPOINTS, CourseExtended } from "@kalabo/golfsavers-common";
import { Text } from "@uikit";
import { getCoursePricing, slugify } from "@kalabo/golfsavers-common";

import {
  DisableScroll,
  EnableScroll,
  isRegionalSite,
  useRootCourseUrls,
} from "@utilities/core";

import XE from "../xe";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import { IoSearchOutline } from "@react-icons/all-files/io5/IoSearchOutline";
import { useApp } from "@store/app";
import { HotelExtended } from "@kalabo/golfsavers-common";
import useBreakpoint from "use-breakpoint";
import { has } from "lodash";
import { useDocument } from "@hooks/useDocument";

// The maximum number of items we want to show in the list
const maxItems = 6;

interface IItemProps {
  appearsInDefaultListbox: boolean;
  index: number;
  item: any;
  query: string;
  searchType: string;
  setSelected: any;
  totalItems: number;
}

// Custom Item component
const Item = (props: IItemProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const date = useApp(($) => $.date);
  const {
    appearsInDefaultListbox,
    index,
    item,
    query,
    searchType = "startswith",
    totalItems,
  } = props;

  const globalMatch = searchType === "contains";

  const SplitComponent = (props: any) => {
    const { children, index: splitIndex } = props;
    const doubleLine = index === 0 && !appearsInDefaultListbox;
    const fontWeight = appearsInDefaultListbox
      ? "font-semibold"
      : totalItems === 1
      ? "font-semibold"
      : searchType === "startswith"
      ? "font-semibold"
      : "font-semibold";
    const display = doubleLine ? "block" : "inline";
    const lineHeight = doubleLine ? "leading-6" : "";
    const textSize = doubleLine
      ? splitIndex === 1
        ? "text-base"
        : "text-xl"
      : splitIndex === 1
      ? "text-sm"
      : "text-lg";
    const className = `${display} ${fontWeight} ${textSize} ${lineHeight}`;
    return <span className={className}>{children}</span>;
  };

  const MatchComponent = (props: any) => {
    const { children } = props;
    const className = appearsInDefaultListbox
      ? "font-semibold"
      : totalItems === 1
      ? "font-bold"
      : searchType === "startswith"
      ? "font-bold"
      : " font-bold";
    return <span className={className}>{children}</span>;
  };

  const matchedText = (includeSeparator: any) => {
    return (
      <SplitMatch
        searchText={query}
        globalMatch={globalMatch}
        globalSplit={false}
        includeSeparator={includeSeparator}
        MatchComponent={MatchComponent}
        SplitComponent={SplitComponent}
      >
        {item.name}
      </SplitMatch>
    );
  };

  return item.type === "course" ? (
    <CourseItem
      item={item}
      matchedText={matchedText(true)}
      display_currency={display_currency}
      date={date}
    />
  ) : (
    <HotelItem item={item} matchedText={matchedText(true)} />
  );
};

const CourseItem = ({ item, display_currency, date, matchedText }: any) => {
  let price_obj = getCoursePricing(null, item.golf_contracts) || 0;
  let images = item.images;
  let featured_image = images?.length > 0 ? images[0].url : null;
  const savingsPill = (rate: any) => {
    let sell_price = Number(rate.price);

    if (sell_price < Number(rate.reference_rate)) {
      let savings_percent = Math.round(
        ((Number(rate.reference_rate) - sell_price) /
          Number(rate.reference_rate)) *
          100
      );
      return (
        <span className="bg-red rounded-sm text-white text-center font-semibold px-1  text-10 sm:text-11 self-center ml-1">
          SAVE {savings_percent}%
        </span>
      );
    }
  };
  return (
    <div className="flex gap-2 items-center hover:bg-grey">
      <img
        className="w-[70px] rounded-[4px]"
        src={`${process.env.GATSBY_IMAGE_PREFIX}${featured_image}?aspect_ratio=1:1&width=70&height=70)`}
      />
      <div className="flex flex-col gap-1 overflow-hidden">
        <Text
          element="span"
          size={15}
          color="black90"
          weight="semibold"
          leading={1.3}
          className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
        >
          {matchedText || item.name}
        </Text>
        <div className="flex items-center">
          {price_obj.price === 0 ? (
            <>
              <Text size={13} color="black90" leading={1.3}>
                Enquire for price
              </Text>
            </>
          ) : (
            <div className="flex  gap-1 flex-1 items-end">
              <Text
                size={9}
                italic
                color="black90"
                leading={1.5}
                className="pb-[1px]"
              >
                from
              </Text>
              <div className="flex items-end gap-1">
                <Text size={13} color="black90" leading={1.3}>
                  <XE
                    base_currency={price_obj?.currency}
                    simplify
                    currency={display_currency.code}
                    value={Math.round(Number(price_obj.price) * 100) / 100}
                  />
                </Text>
                <Text size={13} color="black90" leading={1.3}>
                  / 18H
                </Text>
                {savingsPill(price_obj)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HotelItem = ({ item, matchedText }: any) => {
  let images = item.images;
  let featured_image = images?.length > 0 ? images[0].url : null;
  return (
    <div className="flex gap-2 items-center hover:bg-grey">
      <img
        className="w-[70px] rounded-[4px]"
        src={`${process.env.GATSBY_IMAGE_PREFIX}${featured_image}?aspect_ratio=1:1&width=70&height=70)`}
      />
      <div className="flex flex-col gap-1 overflow-hidden">
        <Text
          element="span"
          size={15}
          color="black90"
          weight="semibold"
          leading={1.3}
          className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
        >
          {matchedText || item.name}
        </Text>
      </div>
    </div>
  );
};

const Cancel = () => <XIcon className="w-6 h-6 text-black/80" />;
const Clear = () => <XIcon type="clear" className="w-6 h-6" />;

interface CommandPaletteProps {
  homepage?: boolean;
  region_id?: string;
}

const CommandPalette = ({
  homepage = false,
  region_id,
}: CommandPaletteProps) => {
  const [hasFocus, setHasFocus] = useState(false);
  const display_currency = useApp(($) => $.display_currency);
  const date = useApp(($) => $.date);
  const doc = useDocument();

  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");

  const [hasInputFocus, setHasInputFocus] = useState(false);

  // Style the container so on mobile devices the search box and results
  // take up the whole screen
  const containerStyles = hasFocus
    ? "fixed block w-full h-full top-0 left-0 bg-white z-[9999] overflow-auto sm:relative  sm:h-auto sm:top-auto sm:left-auto sm:bg-transparent sm:z-auto sm:overflow-visible"
    : "relative w-full z-[99] ";

  const iconDisplayStyle = hasFocus
    ? "hidden text-crystal-600"
    : "inline-flex text-crystal-500";

  const onBlur = () => setHasInputFocus(false);
  const onFocus = () => setHasInputFocus(true);

  React.useEffect(() => {
    if (hasFocus && breakpoint === "mobile") {
      DisableScroll(doc);
    } else {
      EnableScroll(doc);
    }
  }, [hasFocus, breakpoint, doc]);

  const data = useStaticQuery(graphql`
    query {
      regions: allGolfsaversRegions {
        edges {
          node {
            id
            name {
              en
            }
            slug
            country {
              id
              name {
                en
              }
              slug
            }
            top_courses
            region_id
          }
        }
      }
      hotels: allGolfsaversHotels {
        edges {
          node {
            images {
              alt
              url
            }
            is_recommended
            id
            name {
              en
            }
            region_id
            slug
            region {
              id
              name {
                en
              }
              country {
                id
              }
            }
          }
        }
      }
      courses: allGolfsaversGolfCourses(filter: { published: { eq: true } }) {
        edges {
          node {
            images {
              alt
              url
            }
            region_id
            is_recommended
            id
            course_id
            slug
            name {
              en
            }
            region {
              id
              slug
              name {
                en
              }
              country {
                id
                name {
                  en
                }
                slug
              }
            }
            golf_contracts {
              type
              supplier_id
              start_date
              prepaid_inventory
              platforms
              payment_terms
              modified_at
              id
              products {
                caddie_price
                caddie_price_enabled
                contract_id
                created_at
                id
                modified_at
                note
                walk_in_rate
                single_cart_price
                single_cart_enabled
                shared_cart_price
                shared_cart_enabled
                product_id
                odd_number_surcharge_enabled
                product {
                  config {
                    transfers
                    single_cart
                    shared_cart
                    shared_caddie
                    meals
                    caddie
                    holes
                    all_inclusive
                  }
                  type
                  sku
                  name {
                    en
                  }
                  modified_by
                  modified_at
                  id
                  created_by
                  created_at
                }
                rates {
                  availability_days {
                    fri
                    hol
                    mon
                    sat
                    sun
                    thu
                    tue
                    wed
                  }
                  sell_price
                  reference_rate
                  product_type_id
                  multiples
                  modified_at
                  min_pax
                  max_pax
                  margin
                  latest_time
                  id
                  earliest_time
                  created_at
                  cost_price
                  contract_product_id
                  cart_policy
                  caddie_policy
                }
              }
              end_date
              currency_code
              created_at
              course_id
              booking_window_start_date
              booking_window_end_date
            }
          }
        }
      }
    }
  `);

  let current_region = data.regions.edges?.find(
    (region: any) =>
      region.node.region_id === (process.env.GATSBY_REGION_ID || region_id)
  )?.node;

  let golf_courses = data.courses.edges
    .map((a: any) => a.node)
    .filter((a: any) =>
      a.region_id === region_id ? region_id : process.env.GATSBY_REGION_ID
    );

  let hotels = data.hotels.edges
    .map((a: any) => a.node)
    .filter((a: any) =>
      a.region_id === region_id ? region_id : process.env.GATSBY_REGION_ID
    );

  const defaultListbox = [
    {
      id: "courses",
      name: "Popular searches",
      ratio: 3,
      displayField: "name",
      data: golf_courses
        .map((course: CourseExtended) => {
          return {
            name: course.name.en,
            slug: course.slug,
            course_id: course.course_id,
            images: course.images,
            golf_contracts: course.golf_contracts,
            is_recommended: course.is_recommended,
            type: "course",
          };
        })
        .sort((a: any, b: any) => {
          let a_course = current_region?.top_courses?.indexOf(a.course_id);
          if (a_course === -1) {
            a_course = 999;
          }
          let b_course = current_region?.top_courses?.indexOf(b.course_id);
          if (b_course === -1) b_course = 999;

          return a_course - b_course;
        }),
      searchType: "contains",
    },
  ];

  const listbox = [
    {
      id: "courses",
      name: "Courses",
      ratio: 4,
      displayField: "name",
      data: golf_courses
        .map((course: CourseExtended) => {
          return {
            name: course.name.en,
            slug: course.slug,
            course_id: course.course_id,
            images: course.images,
            golf_contracts: course.golf_contracts,
            is_recommended: course.is_recommended,
            type: "course",
          };
        })
        .sort((a: any, b: any) => {
          let a_course = current_region?.top_courses?.indexOf(a.course_id);
          if (a_course === -1) {
            a_course = 999;
          }
          let b_course = current_region?.top_courses?.indexOf(b.course_id);
          if (b_course === -1) b_course = 999;

          return a_course - b_course;
        }),
      searchType: "contains",
    },
    {
      id: "hotels",
      name: "Hotels",
      ratio: 4,
      displayField: "name",
      data: hotels.map((hotel: HotelExtended) => {
        return {
          name: hotel.name.en,
          slug: hotel.slug,
          hotel_id: hotel.hotel_id,
          images: hotel.images,
          //golf_contracts: course.golf_contracts,
          is_recommended: hotel.is_recommended,
          type: "hotel",
        };
      }),
      searchType: "contains",
    },
  ];

  const searchSuggestions = React.useCallback(() => {
    return (
      <div className="text-left">
        {defaultListbox.map((group: any) => {
          return (
            <div>
              <div className="cursor-default mt-5 pt-[1px] mb-0.5 px-1.5 text-black/70 font-semibold">
                {group.name}
              </div>
              {group.data.slice(0, 6).map((item: any) => {
                return (
                  <div
                    className={
                      "cursor-pointer p-1.5 text-lg whitespace-nowrap text-ellipsis overflow-hidden text-black"
                    }
                    onClick={() => {
                      if (item.type === "course") {
                        navigate(
                          isRegionalSite()
                            ? `/${
                                useRootCourseUrls()
                                  ? item.slug
                                  : `${slugify(
                                      current_region.name.en.toLowerCase()
                                    )}-golf-courses/${item.slug}`
                              }`
                            : `/${slugify(
                                current_region.country.slug
                              )}/${slugify(
                                current_region.name.en.toLowerCase()
                              )}-golf-courses/${item.slug}`
                        );
                      } else {
                        navigate(
                          isRegionalSite()
                            ? `/${slugify(
                                current_region.name.en.toLowerCase()
                              )}-golf-packages/${item.slug}`
                            : `/${slugify(
                                current_region.country.slug
                              )}/${slugify(
                                current_region.name.en.toLowerCase()
                              )}-golf-packages/${item.slug}`
                        );
                      }
                    }}
                  >
                    {item.type === "course" ? (
                      <CourseItem
                        item={item}
                        display_currency={display_currency}
                        date={date}
                      />
                    ) : (
                      <HotelItem item={item} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        <Link
          to={
            isRegionalSite()
              ? `/${slugify(current_region.slug)}-golf-courses`
              : `/${slugify(current_region.country.slug)}/${slugify(
                  current_region.slug
                )}-golf-courses`
          }
          className="underline"
          aria-label={`See all ${process.env.GATSBY_REGION} golf courses`}
        >
          <Text
            size={15}
            color="black"
            weight="semibold"
            leading={1.3}
            className="mt-6"
          >
            See all {process.env.GATSBY_REGION} golf courses{" "}
            <span>
              <ChevronRightIcon className="w-4 inline-block" />
            </span>
          </Text>
        </Link>
      </div>
    );
  }, [defaultListbox, display_currency, date]);

  const onSelect = React.useCallback((item: any, displayField: any) => {
    if (!item) return;
    if (item.type === "course") {
      navigate(
        isRegionalSite()
          ? useRootCourseUrls()
            ? `/${item.slug}`
            : `/${slugify(current_region.slug)}-golf-courses/${item.slug}`
          : `/${slugify(current_region.country.slug)}/${slugify(
              current_region.slug
            )}-golf-courses/${item.slug}`
      );
    } else {
      navigate(
        isRegionalSite()
          ? `/${slugify(current_region.slug)}-golf-packages/${item.slug}`
          : `/${slugify(current_region.country.slug)}/${slugify(
              current_region.slug
            )}-golf-packages/${item.slug}`
      );
    }
  }, []);

  return (
    <div className={containerStyles}>
      {hasFocus && (
        <div className="sm:hidden text-center p-4 border-b border-grey3 relative">
          <Text
            element="h2"
            size={19}
            leading={1.3}
            weight="semibold"
            color="black"
            className="text-center"
          >
            Where do you want to play?
          </Text>
          <div className="absolute right-5 top-4">
            <div
              className="-m-2 p-2 rounded-[6px] inline-flex items-center justify-center text-gray-400"
              onClick={() => setHasFocus(false)}
            >
              <span className="sr-only">Close menu</span>
              <IoCloseOutline
                className="h-6 w-6 text-grey7"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      )}
      <div
        className={` ${
          hasFocus ? "p-4 pt-6 sm:pt-4 sm:p-0" : ""
        } relative  rounded-[6px]`}
      >
        {!hasFocus && (
          <div
            className={`absolute inset-0 z-[99] block: sm:hidden`}
            onClick={() => {
              setHasFocus(true);
            }}
          ></div>
        )}
        <span
          className={`absolute ${
            homepage ? "w-[50px] h-[50px]" : "w-[36px] h-[36px]"
          } inset-y-0
          ${homepage ? "bg-red right-0" : "left-0"}
          items-center justify-center rounded-r-[6px]   z-10 sm:inline-flex ${iconDisplayStyle} ${
            hasInputFocus ? "!rounded-b-none" : ""
          }`}
        >
          {" "}
          <IoSearchOutline
            className={`pointer-events-none  ${
              homepage ? "text-white  h-5 w-5" : "text-grey7 h-5 w-5 mr-[-1px]"
            }`}
            aria-hidden="true"
          />
        </span>

        <Turnstone
          cancelButton={false}
          clearButton={true}
          debounceWait={250}
          id="autocomplete"
          defaultListbox={defaultListbox}
          listbox={listbox}
          listboxIsImmutable={true}
          matchText={true}
          maxItems={maxItems}
          noItemsMessage="Sorry, no results found. Please try refining your search."
          onSelect={onSelect}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={
            breakpoint === "mobile"
              ? `Search for a course or hotel`
              : homepage
              ? `Search for a course or hotel`
              : `Course or hotel name`
          }
          plugins={[
            [RecentSearchesPlugin, { storageKey: "turnstoneExample2" }],
          ]}
          styles={{
            input: `autocomplete-input w-full !py-2 !pr-9  text-xl text-black !outline-none !rounded-[6px]
          ${
            hasFocus
              ? homepage
                ? "h-[50px] bg-white" // FOCUS ON HOMEPAGE
                : "h-[50px] sm:h-[36px] bg-white" // FOCUS ON NON-HOMEPAGE
              : homepage
              ? "h-[50px] bg-white" // NOT FOCUS ON HOMEPAGE
              : "h-[36px] bg-grey !w-[0px] sm:!w-full px-0 sm:pl-9" // NOT FOCUS ON NON-HOMEPAGE
          }`,

            inputFocus: `autocomplete-input w-full !py-2 !pr-9  text-xl text-black !outline-none  !rounded-[6px] sm:!rounded-b-none  sm:!ring-0   !border-grey3
            ${
              hasFocus
                ? homepage
                  ? "h-[50px] bg-white" // FOCUS ON HOMEPAGE
                  : "h-[50px] bg-white sm:h-[36px]" // FOCUS ON NON-HOMEPAGE
                : homepage
                ? "h-[50px] bg-white" // NOT FOCUS ON HOMEPAGE
                : "h-[36px] bg-grey !w-[0px] sm:!w-full px-0  sm:pl-9" // NOT FOCUS ON NON-HOMEPAGE
            }`,
            query: `text-black placeholder-grey5 text-16  ${
              homepage ? "sm:text-15" : "sm:!text-13"
            }`,
            typeahead: `text-grey5 border-white  ${
              homepage ? "sm:text-15" : "sm:!text-13"
            }`,
            cancelButton: `absolute w-10 ${
              homepage ? " h-[50px]" : " h-[40px]"
            } inset-y-0 -top-[70px] -right-2 items-center justify-center z-10 text-crystal-600 inline-flex sm:hidden`,
            clearButton: `${
              hasFocus ? "" : "hidden"
            } absolute inset-y-0 right-0 w-12 inline-flex items-center justify-center [&>svg]:bg-grey5 [&>svg]:text-white [&>svg]:rounded-full [&>svg]:p-1 [&>svg]:w-[20px] [&>svg]:h-[20px]`,
            listbox:
              "w-full bg-white z-[99]   text-left  sm:p-2 sm:drop-shadow-xl mt-[1px] sm:mt-0",
            groupHeading:
              "cursor-default mt-5 mb-0.5 px-1.5 font-semibold text-sm text-black/70",
            item: "cursor-pointer p-1.5 text-lg whitespace-nowrap text-ellipsis overflow-hidden text-black",
            highlightedItem:
              "cursor-pointer p-1.5 text-lg whitespace-nowrap sm:text-ellipsis overflow-hidden text-red rounded-[6px] ",
            noItems: "cursor-default text-center my-20",
          }}
          Cancel={Cancel}
          Clear={Clear}
          Item={Item}
        />

        {!hasInputFocus && hasFocus && (
          <div className="block sm:hidden">{searchSuggestions()}</div>
        )}
      </div>
    </div>
  );
};

export default CommandPalette;
